import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { PrimaryButton } from '../../ui/common';

// Rezerwacja według klas w widgecie (26.10.2026, migracja 069). Firma przełącza wybrane kategorie
// w tryb „klient wybiera klasę”: ustala cenę za dzień każdej klasy i pytania do doboru (wzrost,
// waga…). Kategoria bez żadnej klasy z ceną nie pojawi się w widgecie w tym trybie. Pozostałe
// kategorie działają jak dotąd (klient wybiera konkretny sprzęt).
// API: GET/PUT /api/class-booking/settings (Login/backend/routes/classBooking.js).

// Pytania proponowane po włączeniu kategorii, wg nazwy (lustro reguł utils/doborSprzetu.js).
function domyslnePytania(nazwa) {
  const n = String(nazwa || '').toLowerCase();
  if (/but|boot/.test(n)) return ['shoe_size'];
  if (/kij|pole/.test(n)) return ['height'];
  if (/kask|helmet/.test(n)) return ['head'];
  if (/nart|ski|snowboard|desk/.test(n)) return ['height', 'weight', 'skill'];
  return ['height'];
}

const cenaTekst = (v) => (v == null || v === '' ? '' : String(v).replace('.', ','));
const cenaLiczba = (s) => {
  const t = String(s || '').trim().replace(/\s+/g, '').replace(',', '.');
  if (!t) return null;
  return /^\d+(\.\d{1,2})?$/.test(t) ? Number(t) : NaN;
};

export default function WidgetClassBookingPanel() {
  const { t } = useTranslation();
  const [kandydaci, setKandydaci] = useState([]);
  const [pytania, setPytania] = useState([]);
  // Stan edycji: typeId -> { on, questions: [], prices: { classId: 'tekst' }, over7: { classId: 'tekst' } }
  const [ustawienia, setUstawienia] = useState({});
  const [zapisane, setZapisane] = useState('');
  const [stan, setStan] = useState('loading');
  const [zapis, setZapis] = useState({ trwa: false, tekst: '', blad: false });

  const zKonfiguracji = (kand, konf) => {
    const wynik = {};
    for (const typ of kand) {
      const k = (konf || []).find((x) => x.type_id === typ.type_id);
      wynik[typ.type_id] = {
        on: !!k,
        questions: k ? k.questions : domyslnePytania(typ.type_name),
        prices: Object.fromEntries((k ? k.classes : []).map((c) => [c.class_id, cenaTekst(c.price)])),
        // 070: stawka przy wynajmie dłuższym niż 7 dni, pusta = zwykła stawka.
        over7: Object.fromEntries((k ? k.classes : []).map((c) => [c.class_id, cenaTekst(c.price_over_7)])),
      };
    }
    return wynik;
  };

  const doZapisu = (u) => kandydaci
    .filter((typ) => u[typ.type_id] && u[typ.type_id].on)
    .map((typ) => ({
      type_id: typ.type_id,
      questions: u[typ.type_id].questions,
      classes: typ.classes
        .map((c) => ({
          class_id: c.class_id,
          price: u[typ.type_id].prices[c.class_id] || '',
          ...(String((u[typ.type_id].over7 || {})[c.class_id] || '').trim() ? { price_over_7: u[typ.type_id].over7[c.class_id] } : {}),
        }))
        .filter((c) => String(c.price).trim() !== ''),
    }));

  useEffect(() => {
    let anulowane = false;
    fetch(`${API_BASE}/api/class-booking/settings`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        if (anulowane) return;
        const kand = Array.isArray(d.candidates) ? d.candidates : [];
        setKandydaci(kand);
        setPytania(Array.isArray(d.questions) ? d.questions : []);
        const u = zKonfiguracji(kand, d.class_booking);
        setUstawienia(u);
        setZapisane(JSON.stringify(d.class_booking || []));
        setStan('ready');
      })
      .catch(() => { if (!anulowane) setStan('error'); });
    return () => { anulowane = true; };
  }, []);

  if (stan === 'loading') return <Komunikat>{t('classBooking.loading')}</Komunikat>;
  if (stan === 'error') return <Komunikat $blad>{t('classBooking.loadError')}</Komunikat>;
  if (!kandydaci.length) return <Komunikat>{t('classBooking.empty')}</Komunikat>;

  const zmien = (typeId, zmiana) => {
    setZapis({ trwa: false, tekst: '', blad: false });
    setUstawienia((u) => ({ ...u, [typeId]: { ...u[typeId], ...zmiana(u[typeId]) } }));
  };
  const przelaczPytanie = (typeId, klucz) => zmien(typeId, (s) => ({
    questions: s.questions.includes(klucz)
      ? s.questions.filter((k) => k !== klucz)
      : pytania.map((p) => p.key).filter((k) => k === klucz || s.questions.includes(k)),
  }));

  const bledneCeny = kandydaci.some((typ) => ustawienia[typ.type_id]?.on && typ.classes.some((c) => {
    const v = cenaLiczba(ustawienia[typ.type_id].prices[c.class_id]);
    const d = cenaLiczba((ustawienia[typ.type_id].over7 || {})[c.class_id]);
    return Number.isNaN(v) || v === 0 || Number.isNaN(d) || d === 0;
  }));
  // Włączona kategoria bez ceny żadnej klasy: serwer jej nie zapisze (nie ma czego pokazać
  // w widgecie), więc po zapisie wracała jako wyłączona przy komunikacie „Zapisane”.
  // Zapis czeka, aż firma poda cenę albo wyłączy kategorię.
  const bezCen = kandydaci.filter((typ) => ustawienia[typ.type_id]?.on
    && !typ.classes.some((c) => String(ustawienia[typ.type_id].prices[c.class_id] || '').trim() !== ''));
  const plan = doZapisu(ustawienia);
  const zmienione = JSON.stringify(plan.map((x) => ({
    ...x,
    classes: x.classes.map((c) => ({
      class_id: c.class_id,
      price: cenaLiczba(c.price),
      ...(c.price_over_7 != null ? { price_over_7: cenaLiczba(c.price_over_7) } : {}),
    })),
  }))) !== zapisane;

  const zapisz = async () => {
    setZapis({ trwa: true, tekst: '', blad: false });
    try {
      const r = await fetch(`${API_BASE}/api/class-booking/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': localStorage.getItem('csrfToken') || '' },
        body: JSON.stringify({ class_booking: plan }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('classBooking.saveError'));
      setUstawienia(zKonfiguracji(kandydaci, d.class_booking));
      setZapisane(JSON.stringify(d.class_booking || []));
      setZapis({ trwa: false, tekst: t('classBooking.saved'), blad: false });
    } catch (e) {
      setZapis({ trwa: false, tekst: e.message || t('classBooking.saveError'), blad: true });
    }
  };

  return (
    <div data-cy="class-booking-settings">
      <Lista>
        {kandydaci.map((typ) => {
          const s = ustawienia[typ.type_id] || { on: false, questions: [], prices: {}, over7: {} };
          const sztuk = typ.classes.reduce((n, c) => n + c.units, 0);
          const zCena = typ.classes.filter((c) => String(s.prices[c.class_id] || '').trim() !== '').length;
          const id = `class-booking-${typ.type_id}`;
          return (
            <Kategoria key={typ.type_id} $on={s.on} data-cy={`class-booking-type-${typ.type_id}`}>
              <Naglowek>
                <Przelacznik>
                  <input
                    id={id}
                    type="checkbox"
                    role="switch"
                    checked={s.on}
                    onChange={() => zmien(typ.type_id, (x) => ({ on: !x.on }))}
                    data-cy={`class-booking-toggle-${typ.type_id}`}
                  />
                  <label htmlFor={id}>{typ.type_name}</label>
                </Przelacznik>
                <Meta>{t('classBooking.meta', { classes: typ.classes.length, units: sztuk })}</Meta>
              </Naglowek>
              {!s.on && <Opis>{t('classBooking.offHint')}</Opis>}
              {s.on && (
                <Szczegoly>
                  <Blok>
                    <BlokTytul id={`${id}-q`}>{t('classBooking.questionsTitle')}</BlokTytul>
                    <Chipy role="group" aria-labelledby={`${id}-q`}>
                      {pytania.map((p) => {
                        const wl = s.questions.includes(p.key);
                        return (
                          <Chip
                            key={p.key}
                            type="button"
                            aria-pressed={wl}
                            $on={wl}
                            onClick={() => przelaczPytanie(typ.type_id, p.key)}
                            data-cy={`class-booking-question-${typ.type_id}-${p.key}`}
                          >
                            {t(`classBooking.question.${p.key}`)}
                          </Chip>
                        );
                      })}
                    </Chipy>
                    {!s.questions.length && <Uwaga>{t('classBooking.noQuestions')}</Uwaga>}
                  </Blok>
                  <Blok>
                    <CenyNaglowek aria-hidden="true">
                      <BlokTytul>{t('classBooking.pricesTitle')}</BlokTytul>
                      <span>{t('classBooking.perDay')}</span>
                      <span>{t('classBooking.over7')}</span>
                    </CenyNaglowek>
                    <Ceny>
                      {typ.classes.map((c) => {
                        const tekst = s.prices[c.class_id] ?? '';
                        const v = cenaLiczba(tekst);
                        const zla = Number.isNaN(v) || v === 0;
                        const tekst7 = (s.over7 || {})[c.class_id] ?? '';
                        const v7 = cenaLiczba(tekst7);
                        const zla7 = Number.isNaN(v7) || v7 === 0;
                        const pid = `${id}-c${c.class_id}`;
                        return (
                          <Cena key={c.class_id}>
                            <label htmlFor={pid}>
                              <span className="klasa">{c.class_name}</span>
                              <span className="sztuki">{t('classBooking.units', { n: c.units })}</span>
                            </label>
                            <PoleCeny $zla={zla}>
                              <input
                                id={pid}
                                inputMode="decimal"
                                value={tekst}
                                placeholder={t('classBooking.pricePlaceholder')}
                                onChange={(e) => zmien(typ.type_id, (x) => ({ prices: { ...x.prices, [c.class_id]: e.target.value } }))}
                                aria-invalid={zla || undefined}
                                aria-label={t('classBooking.priceAria', { name: c.class_name })}
                                data-cy={`class-booking-price-${typ.type_id}-${c.class_id}`}
                              />
                            </PoleCeny>
                            <PoleCeny $zla={zla7}>
                              <input
                                inputMode="decimal"
                                value={tekst7}
                                placeholder={t('classBooking.over7Placeholder')}
                                disabled={!String(tekst).trim()}
                                onChange={(e) => zmien(typ.type_id, (x) => ({ over7: { ...(x.over7 || {}), [c.class_id]: e.target.value } }))}
                                aria-invalid={zla7 || undefined}
                                aria-label={t('classBooking.over7Aria', { name: c.class_name })}
                                data-cy={`class-booking-over7-${typ.type_id}-${c.class_id}`}
                              />
                            </PoleCeny>
                          </Cena>
                        );
                      })}
                    </Ceny>
                    {zCena === 0 && <Uwaga $ton="warn">{t('classBooking.noPrices')}</Uwaga>}
                    <Uwaga>{t('classBooking.pricingHint')}</Uwaga>
                  </Blok>
                </Szczegoly>
              )}
            </Kategoria>
          );
        })}
      </Lista>
      <Stopka>
        <PrimaryButton type="button" onClick={zapisz} disabled={!zmienione || bledneCeny || bezCen.length > 0 || zapis.trwa} data-cy="class-booking-save">
          {zapis.trwa ? t('common.saving') : t('classBooking.save')}
        </PrimaryButton>
        {bledneCeny && <Komunikat $blad>{t('classBooking.badPrice')}</Komunikat>}
        {bezCen.length > 0 && (
          <Komunikat $blad data-cy="class-booking-missing-prices">
            {t('classBooking.missingPrices', { names: bezCen.map((typ) => typ.type_name).join(', ') })}
          </Komunikat>
        )}
        {zapis.tekst && <Komunikat $blad={zapis.blad} role={zapis.blad ? 'alert' : 'status'}>{zapis.tekst}</Komunikat>}
      </Stopka>
    </div>
  );
}

/* ── Wygląd (spójny z WidgetFiltersPanel) ───────────────────────────────────────────────── */

const Lista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Kategoria = styled.li`
  padding: 14px 16px;
  border: 1px solid ${(p) => (p.$on ? 'color-mix(in srgb, var(--primary) 45%, var(--input-border))' : 'var(--input-border)')};
  border-radius: 12px;
  background: ${(p) => (p.$on ? 'var(--card-bg)' : 'transparent')};
`;

const Naglowek = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

// Przełącznik jako prawdziwy checkbox z role="switch": stan czyta czytnik ekranu i klawiatura.
// Selektor `input[type='checkbox']` i zerowa ramka są konieczne: globalne `input` z index.css
// daje ramkę 1px (gałka siedziała krzywo, przy prawej i dolnej krawędzi), a na dotyku
// `input[type="checkbox"]` 24x24 z outline-offset 6px ma tę samą specyficzność co `input`.
const Przelacznik = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  input[type='checkbox'] {
    appearance: none;
    box-sizing: border-box;
    width: 36px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: var(--input-border);
    position: relative;
    cursor: pointer;
    transition: background 0.15s ease;
    margin: 0;
    flex: none;
  }
  input::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
  input[type='checkbox']:checked { background: var(--primary); }
  input[type='checkbox']:checked::after { transform: translateX(16px); }
  input[type='checkbox']:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
  label { font-weight: 700; color: var(--text); cursor: pointer; }

  @media (prefers-reduced-motion: reduce) {
    input, input::after { transition: none; }
  }
`;

const Meta = styled.span`
  font-size: 0.8rem;
  color: var(--text-muted);
`;

const Opis = styled.p`
  margin: 6px 0 0 46px;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const Szczegoly = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 18px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--input-border);
  @media (max-width: 760px) { grid-template-columns: minmax(0, 1fr); }
`;

const Blok = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BlokTytul = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

const Chipy = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.button`
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$on ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$on ? 'var(--info-bg)' : 'transparent')};
  color: ${(p) => (p.$on ? 'var(--primary)' : 'var(--text)')};
  font: inherit;
  font-size: 0.8125rem;
  font-weight: ${(p) => (p.$on ? 600 : 500)};
  cursor: pointer;
  &:hover { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const Ceny = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SIATKA_CEN = 'minmax(0, 1fr) 92px 92px';

const CenyNaglowek = styled.div`
  display: grid;
  grid-template-columns: ${SIATKA_CEN};
  align-items: end;
  gap: 10px;

  > span { font-size: 0.72rem; font-weight: 600; color: var(--text-muted); text-align: right; }
`;

const Cena = styled.div`
  display: grid;
  grid-template-columns: ${SIATKA_CEN};
  align-items: center;
  gap: 10px;

  label { display: flex; flex-direction: column; min-width: 0; cursor: pointer; }
  .klasa { font-weight: 600; color: var(--text); }
  .sztuki { font-size: 0.75rem; color: var(--text-muted); }
`;

const PoleCeny = styled.div`
  display: flex;
  min-width: 0;

  input {
    width: 100%;
    padding: 7px 10px;
    border-radius: 8px;
    border: 1px solid ${(p) => (p.$zla ? 'var(--danger)' : 'var(--input-border)')};
    background: var(--input-bg);
    color: var(--text);
    font: inherit;
    font-size: 0.875rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  input:focus { outline: none; border-color: var(--primary); }
  input:disabled { opacity: 0.5; }
`;

const Uwaga = styled.span`
  font-size: 0.8rem;
  color: ${(p) => (p.$ton === 'warn' ? 'var(--warning)' : 'var(--text-muted)')};
`;

const Stopka = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
`;

const Komunikat = styled.span`
  font-size: 0.875rem;
  color: ${(p) => (p.$blad ? 'var(--danger)' : 'var(--text-muted)')};
`;
